// Builds and keeps the migration timelines of the open folder, and rebuilds them when files change.
import { useEffect } from 'react';
import { create } from 'zustand';
import { findSet, loadModel, refKey, useWorkspace, type ModelRef } from '../state/workspace';
import type { OperationsResult } from '../workers/parse.worker';
import { parser } from '../workers/parseClient';
import { assembleTimeline, type MigrationStepInput, type Timeline } from './model';

export { changeCount, stepIndexOf, type Timeline, type TimelineStep } from './model';

interface TimelineState {
  timelines: Record<string, Timeline>;
  progress: Record<string, { done: number; total: number }>;
}

export const useTimelines = create<TimelineState>(() => ({ timelines: {}, progress: {} }));

const operationsCache = new Map<string, Promise<OperationsResult | null>>();
const building = new Map<string, Promise<void>>();

useWorkspace.subscribe((state, previous) => {
  if (state.source !== previous.source) {
    operationsCache.clear();
    useTimelines.setState({ timelines: {}, progress: {} });
  } else if (state.lastChange && state.lastChange !== previous.lastChange) {
    for (const path of state.lastChange.paths) operationsCache.delete(path);
  }
});

function loadOperations(path: string): Promise<OperationsResult | null> {
  let task = operationsCache.get(path);
  if (!task) {
    task = (async () => {
      const source = useWorkspace.getState().source;
      if (!source) return null;
      try {
        return await parser().operations(await source.read(path));
      } catch {
        return null;
      }
    })();
    operationsCache.set(path, task);
  }
  return task;
}

async function inPool<T>(items: T[], size: number, run: (item: T, index: number) => Promise<void>): Promise<void> {
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      await run(items[i]!, i);
    }
  };
  await Promise.all(Array.from({ length: Math.min(size, items.length) }, worker));
}

async function build(setKey: string): Promise<void> {
  const discovery = useWorkspace.getState().discovery;
  const set = findSet(setKey);
  if (!discovery || !set) return;
  const total = set.migrations.length + (set.snapshotPath ? 1 : 0);
  let done = 0;
  const report = () => useTimelines.setState((s) => ({ progress: { ...s.progress, [setKey]: { done, total } } }));
  if (!useTimelines.getState().timelines[setKey]) report();

  const migrations: MigrationStepInput[] = new Array(set.migrations.length);
  await inPool(set.migrations, 4, async (m, i) => {
    const ref: ModelRef = { setKey, migrationId: m.id };
    const [loaded, operations] = await Promise.all([m.designerPath ? loadModel(ref) : null, m.mainPath ? loadOperations(m.mainPath) : null]);
    migrations[i] = {
      id: m.id,
      name: m.name,
      timestamp: m.timestamp,
      migration: m,
      ref,
      model: loaded?.result.doc.model ?? null,
      file: m.designerPath,
      operations,
      error: m.designerPath ? (loaded ? null : (useWorkspace.getState().modelErrors[refKey(ref)] ?? 'Could not read the model.')) : 'This migration has no .Designer.cs file, so its model is unknown.',
    };
    done++;
    if (done % 4 === 0) report();
  });

  const snapshotRef: ModelRef = { setKey, migrationId: null };
  const snapshot = set.snapshotPath ? await loadModel(snapshotRef) : null;
  const timeline = await assembleTimeline(
    setKey,
    migrations,
    snapshot && set.snapshotPath ? { ref: snapshotRef, model: snapshot.result.doc.model, file: set.snapshotPath } : null,
    discovery,
  );
  useTimelines.setState((s) => {
    const progress = { ...s.progress };
    delete progress[setKey];
    return { timelines: { ...s.timelines, [setKey]: timeline }, progress };
  });
}

/** Builds (or rebuilds after a change in the folder) the timeline of a migration set. */
export function ensureTimeline(setKey: string): Promise<void> {
  const running = building.get(setKey);
  if (running) return running;
  const existing = useTimelines.getState().timelines[setKey];
  const discovery = useWorkspace.getState().discovery;
  if (!discovery || (existing && existing.builtFor === discovery)) return Promise.resolve();
  const task = build(setKey)
    .catch(() => undefined)
    .finally(() => {
      building.delete(setKey);
      // The folder changed while building: build again.
      const now = useWorkspace.getState().discovery;
      if (now && now !== discovery && findSet(setKey)) void ensureTimeline(setKey);
    });
  building.set(setKey, task);
  return task;
}

/** The timeline of a migration set; `build` starts building it when missing or out of date. */
export function useTimeline(setKey: string | null, build: boolean): Timeline | undefined {
  const timeline = useTimelines((s) => (setKey ? s.timelines[setKey] : undefined));
  const discovery = useWorkspace((s) => s.discovery);
  const stale = !!timeline && timeline.builtFor !== discovery;
  useEffect(() => {
    if (setKey && discovery && (build ? !timeline || stale : stale)) void ensureTimeline(setKey);
  }, [setKey, discovery, build, timeline, stale]);
  return timeline;
}

export function useTimelineProgress(setKey: string): { done: number; total: number } | undefined {
  return useTimelines((s) => s.progress[setKey]);
}
